import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, signal, effect } from '@angular/core';
import { DecimalPipe, NgIf, NgFor } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { KnobModule } from 'primeng/knob';
import { TooltipModule } from 'primeng/tooltip';
import { DropdownModule } from 'primeng/dropdown';
import { Subject, takeUntil } from 'rxjs';
import { RecruitingGoals, RecruitingProgress } from 'src/app/api/types/registration';
import { DashboardDataService } from '../../../../services/dashboard-data.service';
import { IconService } from 'src/app/ui/icon.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { McSettingsService } from 'src/app/api/mc-settings.service';
import { RegistrationService } from 'src/app/api/registration.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { DateTime } from 'luxon';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

interface WeekMonthStat {
  title: string;
  tooltip: string;
  percentage: number;
  displayPercentage: number;
  value: number;
  goal: number;
  teams: number;
}

interface OtherStat {
  title: string;
  tooltip: string;
  originalPercentage: number;
  originalDisplayPercentage: number;
  originalValue: number;
  originalGoal: number;
  actualPercentage: number;
  actualDisplayPercentage: number;
  actualValue: number;
  actualGoal: number;
  teams: number;
  show: boolean;
}

@Component({
  selector: 'app-registration-progress',
  standalone: true,
  imports: [CardModule, KnobModule, FormsModule, TooltipModule, DecimalPipe, NgIf, NgFor, FontAwesomeModule, DropdownModule],
  templateUrl: './registration-progress.component.html',
  styleUrl: './registration-progress.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegistrationProgressComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  private progress = signal<RecruitingProgress | null>(null);
  private goals = signal<RecruitingGoals | null>(null);

  currentYear = toSignal(this.mcSettingsService.getCurrentRecruitingYear(), { initialValue: new Date().getFullYear() });
  currentMonth = DateTime.now().setZone('America/Chicago').setZone('utc', {keepLocalTime: true}).toFormat('MMMM');
  
  // Year dropdown - will be initialized in ngOnInit
  selectedYear = signal<number>(new Date().getFullYear());
  
  // Generate year options from 2008 to current year + 1
  yearOptions = computed(() => {
    const current = this.currentYear() ?? new Date().getFullYear();
    return Array.from(
      { length: current - 2008 + 2 }, 
      (_, i) => ({ label: (current + 1 - i).toString(), value: current + 1 - i })
    );
  });

  // Computed values for week and month stats
  weekMonthStats = computed(() => {
    const week = this.weekProgress();
    const month = this.monthProgress();
    
    return [
      {
        title: 'Week',
        tooltip: 'All trips registered this week, any trip year, no transfers',
        percentage: week.percentage,
        displayPercentage: week.displayPercentage,
        value: week.value,
        goal: week.goal,
        teams: week.teams
      },
      {
        title: `${this.currentMonth}`,
        tooltip: 'All trips (excluding seeded trips) registered this month, any trip year, no transfers',
        percentage: month.percentage,
        displayPercentage: month.displayPercentage,
        value: month.value,
        goal: month.goal,
        teams: month.teams
      }
    ];
  });

  // Computed values for other stats
  otherStats = computed(() => {
    const ppm = this.yearUSProgress();
    const overall = this.yearProgress();
    const global = this.yearGlobalProgress();
    const year = this.selectedYear();
    
    return [
      {
        title: 'PPM Trips',
        tooltip: `All ${year} trips registered during any year, PPM Designation`,
        ...ppm,
        show: true
      },
      {
        title: `Overall ${year}`,
        tooltip: `All ${year} trips registered during any year`,
        ...overall,
        show: true
      },
      {
        title: 'Global/Seeded',
        tooltip: `All ${year} trips registered during any year, Global/Seeded designations`,
        ...global,
        show: global.originalGoal > 0
      }
    ];
  });

  // Computed values for each section
  weekProgress = computed(() => {
    const progress = this.progress()?.freeLunch;
    const goal = this.goals()?.freeLunch;
    if (!progress || !goal) return { percentage: 0, value: 0, goal: 0, teams: 0 };
    return {
      percentage: Math.round((progress.totalOriginal / goal) * 100),
      displayPercentage: this.getDisplayPercentage(Math.round((progress.totalOriginal / goal) * 100)) ?? 0,
      value: progress.totalOriginal,
      goal,
      teams: progress.teamCount
    };
  });

  monthProgress = computed(() => {
    const progress = this.progress()?.regiMonth;
    const goal = this.goals()?.regiMonth;
    if (!progress || !goal) return { percentage: 0, value: 0, goal: 0, teams: 0 };
    return {
      percentage: Math.round((progress.totalOriginal / goal) * 100),
      displayPercentage: this.getDisplayPercentage(Math.round((progress.totalOriginal / goal) * 100)) ?? 0,
      value: progress.totalOriginal,
      goal,
      teams: progress.teamCount
    };
  });

  yearUSProgress = computed(() => {
    const progress = this.progress()?.regiYearUS;
    const goals = this.goals()?.regiYearUS;
    const actualGoals = this.goals()?.actualYearUS;
    if (!progress || !goals || !actualGoals) {
      return {
        originalPercentage: 0,
        originalDisplayPercentage: 0,
        originalValue: 0,
        originalGoal: 0,
        actualPercentage: 0,
        actualDisplayPercentage: 0,
        actualValue: 0,
        actualGoal: 0,
        teams: 0
      };
    }
    return {
      originalPercentage: Math.round((progress.totalOriginal / goals) * 100),
      originalDisplayPercentage: this.getDisplayPercentage(Math.round((progress.totalOriginal / goals) * 100)) ?? 0,
      originalValue: progress.totalOriginal,
      originalGoal: goals,
      actualPercentage: Math.round((progress.totalActual / actualGoals) * 100),
      actualDisplayPercentage: this.getDisplayPercentage(Math.round((progress.totalActual / actualGoals) * 100)) ?? 0,
      actualValue: progress.totalActual,
      actualGoal: actualGoals,
      teams: progress.teamCount
    };
  });

  yearProgress = computed(() => {
    const progress = this.progress()?.regiYear;
    const goals = this.goals()?.regiYear;
    const actualGoals = this.goals()?.actualYear;
    if (!progress || !goals || !actualGoals) {
      return {
        originalPercentage: 0,
        originalDisplayPercentage: 0,
        originalValue: 0,
        originalGoal: 0,
        actualPercentage: 0,
        actualDisplayPercentage: 0,
        actualValue: 0,
        actualGoal: 0,
        teams: 0
      };
    }
    return {
      originalPercentage: Math.round((progress.totalOriginal / goals) * 100),
      originalDisplayPercentage: this.getDisplayPercentage(Math.round((progress.totalOriginal / goals) * 100)) ?? 0,
      originalValue: progress.totalOriginal,
      originalGoal: goals,
      actualPercentage: Math.round((progress.totalActual / actualGoals) * 100),
      actualDisplayPercentage: this.getDisplayPercentage(Math.round((progress.totalActual / actualGoals) * 100)) ?? 0,
      actualValue: progress.totalActual,
      actualGoal: actualGoals,
      teams: progress.teamCount
    };
  });

  yearGlobalProgress = computed(() => {
    const progress = this.progress()?.regiYearGlobal;
    const goals = this.goals()?.regiYearGlobal;
    const actualGoals = this.goals()?.actualYearGlobal;
    if (!progress || !goals || !actualGoals) {
      return {
        originalPercentage: 0,
        originalDisplayPercentage: 0,
        originalValue: 0,
        originalGoal: 0,
        actualPercentage: 0,
        actualDisplayPercentage: 0,
        actualValue: 0,
        actualGoal: 0,
        teams: 0
      };
    }
    return {
      originalPercentage: Math.round((progress.totalOriginal / goals) * 100),
      originalDisplayPercentage: this.getDisplayPercentage(Math.round((progress.totalOriginal / goals) * 100)) ?? 0,
      originalValue: progress.totalOriginal,
      originalGoal: goals,
      actualPercentage: Math.round((progress.totalActual / actualGoals) * 100),
      actualDisplayPercentage: this.getDisplayPercentage(Math.round((progress.totalActual / actualGoals) * 100)) ?? 0,
      actualValue: progress.totalActual,
      actualGoal: actualGoals,
      teams: progress.teamCount
    };
  });

  constructor(
    private dashboardDataService: DashboardDataService,
    public icons: IconService,
    private mcSettingsService: McSettingsService,
    private registrationService: RegistrationService
  ) {
    // Use effects to watch signals
    effect(() => {
      const progress = this.dashboardDataService.registrationProgress();
      this.progress.set(progress);
    });

    effect(() => {
      const goals = this.dashboardDataService.registrationGoals();
      this.goals.set(goals);
    });
    
  }

  ngOnInit() {
    // Initialize data loading
    this.dashboardDataService.initializeData();
    
    // Set initial year to current recruiting year
    const currentYear = this.currentYear();
    if (currentYear) {
      this.selectedYear.set(currentYear);
    }
  }
  
  onYearChange(year: number) {
    if (year && year !== this.selectedYear()) {
      this.selectedYear.set(year);
      this.loadYearlyStats(year);
    }
  }
  
  private loadYearlyStats(year: number) {
    // Only fetch yearly stats, weekly/monthly stay as current
    this.registrationService.getRecruitingProgress(year).pipe(
      takeUntil(this.ngUnsubscribe),
      catchError((error) => {
        // On error, keep existing progress data
        return of(null);
      })
    ).subscribe((progress) => {
      if (progress) {
        // Update only yearly stats, keep weekly/monthly from dashboard service
        const currentProgress = this.progress();
        if (currentProgress) {
          this.progress.set({
            ...currentProgress,
            regiYear: progress.regiYear,
            regiYearUS: progress.regiYearUS,
            regiYearGlobal: progress.regiYearGlobal
          });
        } else {
          this.progress.set(progress);
        }
      }
    });
  }

  private getDisplayPercentage(percentage: number): number {
    if (percentage > 100) percentage = 100;
    return percentage;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
} 